import React, { useState, useRef, useEffect } from "react";
import axios from "axios";
import SmartToyRoundedIcon from "@mui/icons-material/SmartToyRounded";
import PersonRoundedIcon from "@mui/icons-material/PersonRounded";
import SendRoundedIcon from "@mui/icons-material/SendRounded";
import ContentCopyRoundedIcon from "@mui/icons-material/ContentCopyRounded";
import CloseIcon from "@mui/icons-material/Close";
import ReactMarkdown from "react-markdown";
import { Prism as SyntaxHighlighter } from "react-syntax-highlighter";
import { docco } from "react-syntax-highlighter/dist/esm/styles/hljs";
import { toast, ToastContainer, Bounce } from "react-toastify";

export default function Chatbot() {
  const [messages, setMessages] = useState([
    { role: "bot", text: "Hello! How can I help you?" },
  ]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [isChatOpen, setIsChatOpen] = useState(false); // Track whether chatbot is open
  const chatEndRef = useRef(null);
  useEffect(() => {
    const debounceResize = (callback, delay) => {
      let timeout;
      return () => {
        clearTimeout(timeout);
        timeout = setTimeout(callback, delay);
      };
    };

    const handleResize = debounceResize(() => {
      if (chatEndRef.current) {
        chatEndRef.current.scrollIntoView({ behavior: "smooth" });
      }
    }, 100);

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, [messages]);
  //   Scroll to the latest message
  useEffect(() => {
    if (chatEndRef.current) {
      chatEndRef.current.scrollIntoView({ behavior: "smooth" });
    }
  }, [messages]);
  const sendMessage = async () => {
    if (!input.trim()) return;

    const userMessage = { role: "user", text: input };
    setMessages((prev) => [...prev, userMessage]);
    setInput("");
    setLoading(true);

    try {
      // Send the input question to your API
      const response = await axios.post(
        "https://backend.chat.duoples.com/chatbot/interact/",
        { question: input },
        {
          headers: {
            Authorization: `api-key ${process.env.REACT_APP_CHATBOT_API_KEY}`,
            "Content-Type": "application/json",
          },
        }
      );

      // Display the response from the bot
      const botMessage = {
        role: "bot",
        text: response.data.answer || "Sorry, I couldn't find an answer.",
      };
      setMessages((prev) => [...prev, botMessage]);
    } catch (error) {
      console.error("Error fetching API response:", error);
      setMessages((prev) => [
        ...prev,
        { role: "bot", text: "Oops! Something went wrong." },
      ]);
    }

    setLoading(false);
  };

  const copyToClipboard = (text) => {
    navigator.clipboard
      .writeText(text)
      .then(() => {
        toast.success("Code copied to clipboard!", {
          position: "top-center",
          autoClose: 5000,
          hideProgressBar: false,
          closeOnClick: false,
          pauseOnHover: true,
          draggable: true,
          progress: undefined,
          theme: "light",
          transition: Bounce,
        });
      })
      .catch((err) => {
        console.error("Error copying text: ", err);
      });
  };

  return (
    <div>
      {/* Chat Icon to toggle chatbot visibility */}
      <button
        onClick={() => setIsChatOpen((prev) => !prev)}
        className="fixed bottom-5 right-4 bg-blue-600 p-3 rounded-full text-white shadow-lg hover:bg-blue-500"
      >
        <SmartToyRoundedIcon className="w-6 h-6" />
      </button>

      {/* Chatbot container with smooth animation */}
      <div
        className={`fixed flex flex-col h-[500px] bottom-5 right-4 w-[400px] mx-auto bg-gray-900 text-white shadow-lg rounded-lg p-4 transition-all duration-500 transform ease-in-out ${
          isChatOpen
            ? "opacity-100 translate-y-0"
            : "opacity-0 translate-y-10 pointer-events-none"
        }`}
      >
        {/* Close button */}
        <button
          onClick={() => setIsChatOpen(false)}
          className="absolute top-2 right-2 text-white"
        >
          <CloseIcon />
        </button>

        <div className="font-semibold text-lg border-b-2 pb-2 border-gray-500 border-opacity-25">
          DuoChat
        </div>

        <div className="h-96 overflow-y-auto p-1 space-y-4 mt-3">
          {messages.map((msg, index) => (
            <div
              key={index}
              className={`flex items-center ${
                msg.role === "user" ? "justify-end" : "justify-start"
              }`}
            >
              {msg.role === "bot" && (
                <SmartToyRoundedIcon className="w-6 h-6 m-2" />
              )}
              <div
                className={`flex p-3 max-w-[85%] h-fit rounded-xl items-center ${
                  msg.role === "user"
                    ? "bg-blue-500 text-white"
                    : "bg-gray-700 text-white"
                }`}
              >
                {/* Render markdown content */}
                <ReactMarkdown
                  children={typeof msg.text === "string" ? msg.text : ""}
                  components={{
                    pre({ children }) {
                      console.log(children);
                      return (
                        <div className="relative w-full">
                          <pre className="whitespace-pre-wrap break-words w-full bg-gray-800 p-3 rounded-lg">
                            {children}
                          </pre>
                          <div
                            className="flex cursor-pointer mt-2"
                            onClick={() =>
                              copyToClipboard(String(children.props.children))
                            }
                          >
                            <ContentCopyRoundedIcon className="right-2 p-1 text-gray-400" />
                            <div className="text-gray-400 font-medium">
                              copy
                            </div>
                          </div>
                        </div>
                      );
                    },
                    code({ node, inline, className, children, ...props }) {
                      const match = /language-(\w+)/.exec(className || "");
                      return !inline && match ? (
                        <SyntaxHighlighter
                          style={docco}
                          language={match[1]}
                          PreTag="div"
                          className="w-full"
                          {...props}
                        >
                          {String(children).replace(/\n$/, "")}
                        </SyntaxHighlighter>
                      ) : (
                        <code
                          {...props}
                          className={`bg-gray-200 text-sm px-1 rounded ${
                            className || ""
                          }`}
                        >
                          {children}
                        </code>
                      );
                    },
                  }}
                />
              </div>
              {msg.role === "user" && (
                <PersonRoundedIcon className="w-6 h-6 m-2" />
              )}
            </div>
          ))}
          <div ref={chatEndRef}></div>
        </div>

        <div className="flex mt-2 space-x-2">
          <input
            type="text"
            className="flex-1 p-2 bg-gray-800 text-white rounded-lg outline-none"
            placeholder="Type a message..."
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && sendMessage()}
          />
          <button
            onClick={sendMessage}
            className="bg-blue-600 p-2 rounded-full hover:bg-blue-500"
            disabled={loading}
          >
            {loading ? "..." : <SendRoundedIcon />}
          </button>
        </div>
      </div>
      <ToastContainer
        position="top-center"
        autoClose={5000}
        hideProgressBar={false}
        newestOnTop={false}
        closeOnClick={false}
        rtl={false}
        pauseOnFocusLoss
        draggable
        pauseOnHover
        theme="light"
        transition={Bounce}
      />
    </div>
  );
}
